import { useEffect, useMemo, useState } from "react";
import { useWallet } from "../context/WalletContext.jsx";
import { useChainTime } from "../hooks/useChainTime.js";
import { useDeposits } from "../hooks/useDeposits.js";
import config from "../config/index.js";
import { formatUsdcCompact, liveAccruedInterest } from "../utils/format.js";

/**
 * Shared navigation + Investment Summary primitives used by both the
 * desktop Sidebar and the mobile MobileDrawer. Both surfaces render the
 * exact same nav structure and the same live aggregates, so the source
 * of truth (especially the spec-critical borrow-cap math) lives here in
 * one place — the two surfaces only differ in Tailwind sizing wrappers.
 */
export const navItems = [
    { to: "/dashboard", label: "Dashboard", icon: "/images/Nav.svg" },
    // /deposit shares the My TD's tab — opening a new term deposit is part
    // of the same surface as managing existing ones.
    {
        to: "/term-deposits",
        label: "My Term Deposits",
        icon: "/images/Nav1.svg",
        // Detail routes (`/term-deposits/:id`) and the standalone Deposit
        // page both belong to the same surface as the listing.
        matchPrefixes: ["/term-deposits/", "/deposit"]
    },
    {
        to: "/loans",
        label: "Loans",
        icon: "/images/Nav2.svg",
        // /loans/lending and /loans/turbo are reached from the
        // "Borrow New Loan" dropdown on the Loans page — they're the
        // same surface.
        matchPrefixes: ["/loans/"]
    },
    { to: "/reinvest", label: "Auto-Reinvest", icon: "/images/Nav7.svg" },
    { to: "/my-tokens", label: "My Tokens", icon: "/images/Nav3.svg" },
    { to: "/activity", label: "Activity", icon: "/images/Nav4.svg" },
    { to: "/settings", label: "Settings", icon: "/images/Nav5.svg" },
    { to: "/support", label: "Help / Support", icon: "/images/Nav6.svg" }
];

/**
 * Live aggregates for the Investment Summary card, shared by Sidebar and
 * MobileDrawer. Owns its own 1-second tick, chain time, and the
 * wallet/deposit hooks so each surface only consumes the result.
 *
 * The two value rows (Net Yield, Available to borrow) sum only Active
 * (state=1) positions — closed / exited deposits have already returned
 * their principal to the user's wallet, so leaving them in would inflate
 * the figures. `tokens` is a position count and intentionally still spans
 * every state; `activeCount` is the subset that is still Active.
 *
 * @returns {{tokens:number, activeCount:number, netYield:string,
 *   safeVault:bigint, showShimmer:boolean}}
 */
export function useInvestmentSummary() {
    const { address } = useWallet();
    const { deposits, isLoading } = useDeposits(address);
    // Shimmer placeholder is only useful while we're actually waiting on
    // an authenticated fetch — for a disconnected wallet there's nothing
    // to load and showing skeletons would be confusing.
    const showShimmer = Boolean(address) && isLoading;

    // 1-second ticker so the live-computed Net Yield actually advances —
    // without it the summary useMemo recomputes only when `deposits`
    // changes, leaving the figure frozen at its mount-time value.
    const [tick, setTick] = useState(0);
    useEffect(() => {
        const id = setInterval(() => setTick((t) => t + 1), 1000);
        return () => clearInterval(id);
    }, []);
    // Chain time, not wall-clock — keeps local-node testing
    // (Hardhat evm_increaseTime) correct. See useChainTime for context.
    const { chainNowSec } = useChainTime();

    const summary = useMemo(() => {
        const activeDeposits = deposits.filter((d) => Number(d.state) === 1);
        // Accrued interest computed the SAME way the contract does it —
        // per whole elapsed day (`liveAccruedInterest`). The indexed
        // `accruedInterest` only moves on a chain event, so we replay the
        // formula — but day-stepped, never per-second, so Net Yield
        // never shows interest the contract hasn't credited.
        const nowSec = chainNowSec;
        let interest = 0n;
        // §V6.2 — "Available to borrow" counts ONLY Standard
        // deposits. Turbo deposits revert any user-driven loan
        // (`NoBorrowingAgainstTurbo`), so summing their principal
        // into the cap shows phantom headroom the user can't
        // actually draw. Filter on the immutable productMode.
        let standardPrincipal = 0n;
        let standardLoan = 0n;
        for (const d of activeDeposits) {
            // Net Yield accrues on every active position (both Standard
            // and Turbo earn yield), so the interest sum stays
            // product-agnostic — it's only the borrow cap that
            // excludes Turbo. Real rate = 20% for both (Turbo's 27.5% is
            // a display-only label).
            interest += liveAccruedInterest(
                d.principal,
                d.startTime,
                d.maturityTime,
                nowSec,
                Number(d.annualRateBps) || config.protocol.annualRateBps,
                d.realizedInterest
            );
            if (Number(d.productMode) === 1) continue; // Turbo — no user borrow
            standardPrincipal += BigInt(d.principal || 0);
            standardLoan += BigInt(d.loanBalance || 0);
        }
        // Borrowable HEADROOM = (Standard safeVault × maxLtv) − already
        // drawn. Subtracting the loan makes the figure match what the
        // user could actually draw right now (not the gross cap).
        const cap =
            (standardPrincipal * BigInt(config.protocol.safeVaultBps) * BigInt(config.protocol.maxLtvBps)) /
            (10000n * 10000n);
        const availableToBorrow = cap > standardLoan ? cap - standardLoan : 0n;
        return {
            tokens: deposits.length,
            activeCount: activeDeposits.length,
            netYield: `$${formatUsdcCompact(interest)}`,
            safeVault: availableToBorrow
        };
        // `tick` is intentionally in the dep list — it forces a fresh
        // `chainNowSec` evaluation every second between RPC polls.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deposits, chainNowSec, tick]);

    return { ...summary, showShimmer };
}

/**
 * Renders a nav icon by masking the parent's `currentColor` through the
 * SVG file. The icon picks up whatever text colour the active / hover /
 * inactive NavLink state defines, so a single SVG asset works across all
 * states. Each surface passes its own `size` (12 desktop / 14 mobile).
 */
export function NavMaskIcon({ src, size = 16 }) {
    return (
        <span
            aria-hidden="true"
            className="inline-block shrink-0"
            style={{
                width: size,
                height: size,
                backgroundColor: "currentColor",
                WebkitMaskImage: `url(${src})`,
                maskImage: `url(${src})`,
                WebkitMaskRepeat: "no-repeat",
                maskRepeat: "no-repeat",
                WebkitMaskPosition: "center",
                maskPosition: "center",
                WebkitMaskSize: "contain",
                maskSize: "contain"
            }}
        />
    );
}

export function SummaryRow({ label, value, valueColor = "text-white" }) {
    return (
        <li className="flex items-center justify-between border-b border-white/5 pb-2 last:border-0 last:pb-0">
            <span className="text-white/70">{label}</span>
            <span className={`font-semibold ${valueColor}`}>{value}</span>
        </li>
    );
}
